import { defineComponent as _defineComponent } from 'vue';
import { createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, unref as _unref, createVNode as _createVNode, renderList as _renderList, Fragment as _Fragment, createBlock as _createBlock, createTextVNode as _createTextVNode, normalizeClass as _normalizeClass } from "vue";
const _hoisted_1 = { class: "mt-4 sm:mt-8" };
const _hoisted_2 = {
    key: 0,
    class: "flex flex-col items-center justify-center py-24",
    "data-testid": "rfm-loading"
};
const _hoisted_3 = { class: "text-sm text-gray-500" };
const _hoisted_4 = {
    key: 1,
    class: "flex flex-col items-center justify-center py-24",
    "data-testid": "rfm-load-error"
};
const _hoisted_5 = { class: "text-sm text-gray-700 mb-4" };
const _hoisted_6 = { class: "text-sm text-gray-600 mb-4" };
const _hoisted_7 = {
    class: "mb-6 rounded-md border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-700",
    role: "status",
    "data-testid": "rfm-recalc-notice"
};
const _hoisted_8 = { class: "text-base font-semibold text-gray-900 mb-3" };
const _hoisted_9 = { class: "grid grid-cols-1 md:grid-cols-4 gap-6 mb-8" };
const _hoisted_10 = ["data-testid"];
const _hoisted_11 = { class: "text-base font-semibold text-gray-900 mb-1" };
const _hoisted_12 = { class: "text-xs text-gray-500 mb-3" };
const _hoisted_13 = { class: "grid grid-cols-1 md:grid-cols-4 gap-6" };
const _hoisted_14 = { class: "grid grid-cols-1 md:grid-cols-4 gap-6 mt-4" };
const _hoisted_15 = { key: 0 };
const _hoisted_16 = { class: "text-base font-semibold text-gray-900 mb-3" };
const _hoisted_17 = {
    key: 1,
    class: "text-sm text-gray-500",
    "data-testid": "rfm-preview-invalid"
};
import { Button, NumberInput, SelectInput, Table } from "@eudonet/opal";
import { computed } from "vue";
import { useI18n } from "vue-i18n";
import { useProfileStore } from "@/modules/settings/stores/useProfileStore";
import { formatDate } from "@/shared/utils/date-formatting";
import { RFM_ANALYSIS_PERIOD_MAX, RFM_ANALYSIS_PERIOD_MIN, RFM_CURRENCIES, RFM_MONETARY_THRESHOLD_MAX, } from "../../../domain/entities/rfmSettings";
import { computeRfmScores, RFM_PREVIEW_PROFILES, } from "../../../domain/services/rfmScorePreview";
export default /*@__PURE__*/ _defineComponent({
    __name: 'RfmTab',
    props: {
        state: {},
        errors: {},
        weightsTotal: {},
        previewSettings: {},
        isLoading: { type: Boolean },
        loadError: { type: Boolean }
    },
    emits: ["update:field", "retry"],
    setup(__props, { emit: __emit }) {
        const props = __props;
        const emit = __emit;
        const { t, locale } = useI18n();
        const profileStore = useProfileStore();
        const DIMENSION_KEYS = ["recency", "frequency", "monetary"];
        const currencyOptions = RFM_CURRENCIES.map((currency) => ({
            value: currency,
            label: currency,
        }));
        const recalcNotice = computed(() => props.state.updatedAt
            ? t("workspace.settings.rfm.recalcNotice", {
                date: formatDate(props.state.updatedAt, locale.value, profileStore.profile?.dateFormat),
            })
            : t("workspace.settings.rfm.recalcNoticeNeverSaved"));
        const analysisPeriodError = computed(() => props.errors.analysisPeriodMonths ? t(props.errors.analysisPeriodMonths) : undefined);
        const currencyError = computed(() => props.errors.currency ? t(props.errors.currency) : undefined);
        const weightsError = computed(() => (props.errors.weights ? t(props.errors.weights) : undefined));
        const dimensions = computed(() => DIMENSION_KEYS.map((key) => {
            // The four thresholds of a dimension are validated as a set, so the message
            // is surfaced once, under the first field.
            const thresholdError = props.errors[key];
            return {
                key,
                title: t(`workspace.settings.rfm.sections.${key}`),
                hint: t(`workspace.settings.rfm.hints.${key}`),
                step: key === "monetary" ? 0.01 : 1,
                // Thresholds must be strictly positive; the smallest monetary amount is one cent.
                min: key === "monetary" ? 0.01 : 1,
                // numeric(10,2) upstream; the other dimensions are counts with no column ceiling.
                max: key === "monetary" ? RFM_MONETARY_THRESHOLD_MAX : undefined,
                weight: props.state[key].weight,
                weightField: `${key}Weight`,
                // One label per dimension: three identical "Weight (%)" labels are ambiguous to a screen reader.
                weightLabel: t(`workspace.settings.rfm.fields.weights.${key}`),
                thresholds: props.state[key].thresholds.map((value, position) => ({
                    value,
                    label: t(`workspace.settings.rfm.fields.${key}Threshold`, { index: position + 1 }),
                    field: `${key}Threshold${position}`,
                    error: position === 0 && thresholdError ? t(thresholdError) : undefined,
                })),
            };
        }));
        const previewColumns = computed(() => ["profile", "recency", "frequency", "monetary", "score"].map((key) => ({
            key,
            label: t(`workspace.settings.rfm.preview.columns.${key}`),
        })));
        const previewRows = computed(() => {
            const settings = props.previewSettings;
            if (!settings)
                return null;
            return RFM_PREVIEW_PROFILES.map((profile) => {
                const scores = computeRfmScores(settings, profile);
                return {
                    id: profile.key,
                    profile: t(`workspace.settings.rfm.preview.profiles.${profile.key}`),
                    recency: scores.recency,
                    frequency: scores.frequency,
                    monetary: scores.monetary,
                    score: scores.combined,
                };
            });
        });
        return (_ctx, _cache) => {
            return (_openBlock(), _createElementBlock("div", _hoisted_1, [
                (__props.isLoading)
                    ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
                        _cache[3] || (_cache[3] = _createElementVNode("i", { class: "ri-loader-4-line animate-spin text-4xl text-gray-400 mb-4" }, null, -1 /* CACHED */)),
                        _createElementVNode("p", _hoisted_3, _toDisplayString(_ctx.$t("workspace.settings.rfm.loading")), 1 /* TEXT */)
                    ]))
                    : (__props.loadError)
                        ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
                            _cache[4] || (_cache[4] = _createElementVNode("i", { class: "ri-error-warning-line text-4xl text-red-400 mb-4" }, null, -1 /* CACHED */)),
                            _createElementVNode("p", _hoisted_5, _toDisplayString(_ctx.$t("workspace.settings.rfm.loadError")), 1 /* TEXT */),
                            _createVNode(_unref(Button), {
                                variant: "secondary",
                                "data-testid": "rfm-retry",
                                label: _ctx.$t('workspace.settings.retry'),
                                onClick: _cache[0] || (_cache[0] = ($event) => (emit('retry')))
                            }, null, 8 /* PROPS */, ["label"])
                        ]))
                        : (_openBlock(), _createElementBlock(_Fragment, { key: 2 }, [
                            _createElementVNode("p", _hoisted_6, _toDisplayString(_ctx.$t("workspace.settings.rfm.intro")), 1 /* TEXT */),
                            _createElementVNode("div", _hoisted_7, _toDisplayString(recalcNotice.value), 1 /* TEXT */),
                            _createElementVNode("h2", _hoisted_8, _toDisplayString(_ctx.$t("workspace.settings.rfm.sections.analysisPeriod")), 1 /* TEXT */),
                            _createElementVNode("div", _hoisted_9, [
                                _createVNode(_unref(NumberInput), {
                                    "model-value": __props.state.analysisPeriodMonths,
                                    label: _ctx.$t('workspace.settings.rfm.fields.analysisPeriodMonths'),
                                    min: _unref(RFM_ANALYSIS_PERIOD_MIN),
                                    max: _unref(RFM_ANALYSIS_PERIOD_MAX),
                                    step: 1,
                                    error: analysisPeriodError.value,
                                    "full-width": "",
                                    "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event) => (emit('update:field', 'analysisPeriodMonths', $event)))
                                }, null, 8 /* PROPS */, ["model-value", "label", "min", "max", "error"])
                            ]),
                            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(dimensions.value, (dimension) => {
                                return (_openBlock(), _createElementBlock("section", {
                                    key: dimension.key,
                                    class: "mb-8",
                                    "data-testid": `rfm-section-${dimension.key}`
                                }, [
                                    _createElementVNode("h2", _hoisted_11, _toDisplayString(dimension.title), 1 /* TEXT */),
                                    _createElementVNode("p", _hoisted_12, _toDisplayString(dimension.hint), 1 /* TEXT */),
                                    _createElementVNode("div", _hoisted_13, [
                                        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(dimension.thresholds, (threshold) => {
                                            return (_openBlock(), _createBlock(_unref(NumberInput), {
                                                key: threshold.field,
                                                "model-value": threshold.value,
                                                label: threshold.label,
                                                min: dimension.min,
                                                max: dimension.max,
                                                step: dimension.step,
                                                error: threshold.error,
                                                "full-width": "",
                                                "onUpdate:modelValue": ($event) => (emit('update:field', threshold.field, $event))
                                            }, null, 8 /* PROPS */, ["model-value", "label", "min", "max", "step", "error", "onUpdate:modelValue"]));
                                        }), 128 /* KEYED_FRAGMENT */))
                                    ]),
                                    _createElementVNode("div", _hoisted_14, [
                                        _createVNode(_unref(NumberInput), {
                                            "model-value": dimension.weight,
                                            label: dimension.weightLabel,
                                            min: 0,
                                            max: 100,
                                            step: 1,
                                            "full-width": "",
                                            "onUpdate:modelValue": ($event) => (emit('update:field', dimension.weightField, $event))
                                        }, null, 8 /* PROPS */, ["model-value", "label", "onUpdate:modelValue"]),
                                        (dimension.key === 'monetary')
                                            ? (_openBlock(), _createBlock(_unref(SelectInput), {
                                                key: 0,
                                                "model-value": __props.state.monetary.currency,
                                                label: _ctx.$t('workspace.settings.rfm.fields.currency'),
                                                options: _unref(currencyOptions),
                                                error: currencyError.value,
                                                "full-width": "",
                                                "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event) => (emit('update:field', 'currency', $event)))
                                            }, null, 8 /* PROPS */, ["model-value", "label", "options", "error"]))
                                            : _createCommentVNode("v-if", true)
                                    ])
                                ], 8 /* PROPS */, _hoisted_10));
                            }), 128 /* KEYED_FRAGMENT */)),
                            _createElementVNode("p", {
                                class: _normalizeClass(["text-sm font-medium mb-8", weightsError.value ? 'text-red-600' : 'text-gray-700']),
                                role: "status",
                                "data-testid": "rfm-weights-total"
                            }, [
                                _createTextVNode(_toDisplayString(_ctx.$t("workspace.settings.rfm.weightsTotal", { total: __props.weightsTotal })) + " ", 1 /* TEXT */),
                                (weightsError.value)
                                    ? (_openBlock(), _createElementBlock("span", _hoisted_15, " — " + _toDisplayString(weightsError.value), 1 /* TEXT */))
                                    : _createCommentVNode("v-if", true)
                            ], 2 /* CLASS */),
                            _createElementVNode("h2", _hoisted_16, _toDisplayString(_ctx.$t("workspace.settings.rfm.sections.preview")), 1 /* TEXT */),
                            (previewRows.value)
                                ? (_openBlock(), _createBlock(_unref(Table), {
                                    key: 0,
                                    columns: previewColumns.value,
                                    rows: previewRows.value,
                                    "show-search": false,
                                    "show-filters": false,
                                    "show-actions": false,
                                    "show-checkbox": false,
                                    "show-add-button": false,
                                    "header-actions-items": [],
                                    "enable-pagination": false
                                }, null, 8 /* PROPS */, ["columns", "rows"]))
                                : (_openBlock(), _createElementBlock("p", _hoisted_17, _toDisplayString(_ctx.$t("workspace.settings.rfm.preview.invalid")), 1 /* TEXT */))
                        ], 64 /* STABLE_FRAGMENT */))
            ]));
        };
    }
});
